<template>
  <div class="cost-breakdown">
    <app-spinner v-if="is_loading"/>
    <template v-else>
      <div class="cost-breakdown__heading">
        <app-cells :indent="false">
          <span
            :class="`cost-breakdown__status--${normalization_status.tone}`"
            class="cost-breakdown__status"
          >
            {{ normalization_status.label }}
          </span>
          <div v-if="source_meta.length" class="cost-breakdown__meta">
            <span v-for="item in source_meta" :key="item">{{ item }}</span>
          </div>
        </app-cells>

        <app-button
          v-if="normalization"
          size="small"
          @click.native="openNormalizationDetails"
        >
          Calculation details
        </app-button>
      </div>

      <div
        v-if="state_banner"
        :class="`cost-breakdown__banner--${state_banner.tone}`"
        class="cost-breakdown__banner"
      >
        <div>
          <strong>{{ state_banner.title }}</strong>
          <p>{{ state_banner.text }}</p>
        </div>
        <button
          v-if="normalization_warnings.length"
          class="cost-breakdown__link-button"
          type="button"
          @click="openWarningsDetails"
        >
          View issue
        </button>
      </div>

      <app-no-results
        v-if="!normalization"
        :margin-top="20"
        :text="empty_state.text"
        :title="empty_state.title"
      />

      <template v-else>
        <div class="info-cards cost-breakdown__metrics">
          <div
            v-for="metric in normalization_metrics"
            :key="metric.label"
            class="info-cards__card cost-breakdown__metric"
          >
            <span class="cost-breakdown__metric-label">{{ metric.label }}</span>
            <strong class="cost-breakdown__metric-value">
              {{ formatMoney(metric.value) }}
            </strong>
            <span class="cost-breakdown__metric-description">{{ metric.description }}</span>
          </div>
        </div>

        <div class="cost-breakdown__table-heading">
          <app-tabs v-model="active_view" :tabs="view_tabs" class="cost-breakdown__tabs"/>
          <span class="cost-breakdown__unit">All amounts in {{ currency }}</span>
        </div>

        <app-input
          v-if="active_view === 'components'"
          v-model="component_search"
          class="input--300 cost-breakdown__search"
          name="story-cost-component-search"
          placeholder="Search"
          type="search"
        />

        <app-no-results
          v-if="!active_rows.length"
          small
          :text="active_view === 'components' && component_search ? 'No component names match your search.' : 'Story did not return any rows for this section.'"
          :title="active_view === 'components' && component_search ? 'Nothing found' : 'No cost data'"
        />
        <div v-else class="cost-breakdown__table table-default">
          <table>
            <thead>
            <tr>
              <th
                v-for="column in active_columns"
                :key="column.key"
                :class="{ 'cost-breakdown__cell--left': column.align === 'left' }"
              >
                {{ column.label }}
              </th>
            </tr>
            </thead>
            <tbody>
            <tr
              v-for="row in active_rows"
              :key="`${active_view}-${row.id}`"
              class="table-default__row"
            >
              <td
                v-for="column in active_columns"
                :key="column.key"
                :class="{ 'cost-breakdown__cell--left': column.align === 'left' }"
              >
                <template v-if="column.type === 'entity'">
                  <span>{{ row.entity_name }}</span>
                </template>
                <span v-else-if="column.type === 'status'">
                    <span
                      :class="`cost-breakdown__status--${row.status_tone}`"
                      class="cost-breakdown__status cost-breakdown__status--small"
                    >
                      {{ row.status_label }}
                    </span>
                  </span>
                <span v-else-if="column.type === 'amount'" class="cost-breakdown__amount">
                    {{ formatAmount(row[column.key]) }}
                  </span>
                <span v-else>{{ displayValue(row[column.key]) }}</span>
              </td>
            </tr>
            </tbody>
            <tfoot>
            <tr>
              <td
                v-for="column in active_columns"
                :key="column.key"
                :class="{ 'cost-breakdown__cell--left': column.align === 'left' }"
              >
                <strong v-if="column.type === 'entity'">{{ active_footer.entity_name }}</strong>
                <strong v-else-if="column.type === 'amount'" class="cost-breakdown__amount">
                  {{ formatAmount(active_footer[column.key]) }}
                </strong>
                <strong v-else>{{ displayValue(active_footer[column.key]) }}</strong>
              </td>
            </tr>
            </tfoot>
          </table>
        </div>

      </template>
    </template>

    <modal
      :adaptive="true"
      :scrollable="true"
      :width="900"
      class="modal-not-overflow"
      height="auto"
      name="story-cost-details"
    >
      <div class="popup">
        <button
          aria-label="Close details"
          class="popup__close"
          type="button"
          @click="$modal.hide('story-cost-details')"
        />
        <div class="cost-breakdown__details-eyebrow">{{ detail.eyebrow }}</div>
        <div class="popup__title">{{ detail.title }}</div>
        <div class="popup__body">
          <p v-if="detail.description" class="cost-breakdown__details-description">
            {{ detail.description }}
          </p>
          <div
            v-for="warning in detail.warnings"
            :key="`${warning.code}-${warning.text}`"
            class="cost-breakdown__detail-warning"
          >
            <code v-if="warning.code">{{ warning.code }}</code>
            <span>{{ warning.text }}</span>
          </div>
          <dl v-if="detail.amounts.length" class="cost-breakdown__details-list">
            <div v-for="item in detail.amounts" :key="item.label">
              <dt>{{ item.label }}</dt>
              <dd>{{ formatMoney(item.value) }}</dd>
            </div>
          </dl>
          <div v-if="detail.comparison.length" class="cost-breakdown__details-table table-default">
            <table>
              <thead>
              <tr>
                <th class="cost-breakdown__cell--left">Amount</th>
                <th>Category</th>
                <th>Lines</th>
                <th>Delta</th>
              </tr>
              </thead>
              <tbody>
              <tr
                v-for="item in detail.comparison"
                :key="item.label"
                class="table-default__row"
              >
                <td class="cost-breakdown__cell--left">{{ item.label }}</td>
                <td>{{ formatAmount(item.category) }}</td>
                <td>{{ formatAmount(item.lines) }}</td>
                <td>{{ formatAmount(item.delta) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <div v-if="detail.meta.length" class="cost-breakdown__details-meta">
            <p v-for="item in detail.meta" :key="item">{{ item }}</p>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import AppTabs from '@/components/AppTabs'
import { getClientsGroupStoryCost } from '@/http'
import { format, parseISO } from 'date-fns'

const VIEW_COLUMNS = {
  categories: [
    {key: 'entity', label: 'Category', type: 'entity', align: 'left'},
    {key: 'lines', label: 'Lines'},
    {key: 'selling', label: 'Selling', type: 'amount'},
    {key: 'profit', label: 'Profit', type: 'amount'},
    {key: 'residual', label: 'Story residual', type: 'amount'},
    {key: 'discount', label: 'Source discount', type: 'amount'},
    {key: 'status', label: 'Check', type: 'status', align: 'left'}
  ],
  components: [
    {key: 'entity', label: 'Service / description', type: 'entity', align: 'left'},
    {key: 'category_name', label: 'Category', align: 'left'},
    {key: 'component_name', label: 'Component', align: 'left'},
    {key: 'selling', label: 'Selling', type: 'amount'},
    {key: 'profit', label: 'Profit', type: 'amount'},
    {key: 'residual', label: 'Story residual', type: 'amount'},
    {key: 'discount', label: 'Source discount', type: 'amount'},
    {key: 'status', label: 'Status', type: 'status', align: 'left'}
  ],
  summary: [
    {key: 'entity', label: 'Cost component', type: 'entity', align: 'left'},
    {key: 'lines', label: 'Lines'},
    {key: 'selling', label: 'Selling', type: 'amount'},
    {key: 'profit', label: 'Profit', type: 'amount'},
    {key: 'residual', label: 'Story residual', type: 'amount'},
    {key: 'discount', label: 'Source discount', type: 'amount'}
  ]
}

const MONEY_FIELDS = [
  ['Selling before personal discount', 'selling_before_personal_discount'],
  ['Story commission profit', 'story_commission_profit'],
  ['Story day-trip discount', 'story_day_trip_discount'],
  ['Recognized profit before personal discount', 'recognized_profit_before_personal_discount'],
  ['Story derived residual', 'story_derived_residual'],
  ['Personal discount · exact', 'personal_discount_exact'],
  ['Personal discount · folio-ceiled', 'personal_discount_folio_ceiled'],
  ['Reported total profit', 'reported_total_profit'],
  ['Expected total profit', 'expected_total_profit_as_is'],
  ['Profit reconciliation delta', 'profit_reconciliation_delta'],
  ['Selling after personal discount · exact', 'selling_after_personal_discount_exact'],
  ['Client folio grand total', 'folio_grand_total'],
  ['Participant rounding delta', 'participant_rounding_delta']
]

const DETAIL_AMOUNT_FIELDS = [
  ['Selling', 'selling_amount'],
  ['Recognized profit', 'recognized_profit_amount'],
  ['Story residual', 'story_derived_residual_amount'],
  ['Source discount', 'source_discount_amount']
]

const STATUS_LABELS = {
  valid: {label: 'Valid', tone: 'green'},
  reconciled: {label: 'Reconciled', tone: 'green'},
  mapped: {label: 'Mapped', tone: 'green'},
  review_required: {label: 'Review required', tone: 'amber'},
  unmapped: {label: 'Unmapped', tone: 'amber'},
  invalid: {label: 'Invalid', tone: 'red'},
  failed: {label: 'Failed', tone: 'red'},
  unavailable: {label: 'Not available', tone: 'neutral'}
}

const UNAVAILABLE_STATES = {
  lead_missing: {
    title: 'No lead linked to this group',
    text: 'Link a lead to the client group. Story cost will appear after the final program has been synced.'
  },
  final_itinerary_missing: {
    title: 'No final itinerary selected',
    text: 'Select a final itinerary for this group. Its Story cost will appear after the program has been synced.'
  },
  program_missing: {
    title: 'No Story program found',
    text: 'The final itinerary does not have a synced Story program yet.'
  },
  normalization_missing: {
    title: 'No cost calculation available',
    text: 'Story has not produced a saved cost normalization for the current program.'
  }
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== ''
}

function normalizeStatus(status, fallback = 'unavailable') {
  const key = String(status || fallback).toLowerCase()
  if (STATUS_LABELS[key]) return STATUS_LABELS[key]
  return {
    label: key.split('_').filter(Boolean).map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`).join(' ') || 'Unknown',
    tone: 'neutral'
  }
}

function normalizeWarnings(warnings) {
  if (!warnings) return []
  const items = Array.isArray(warnings) ? warnings : [warnings]
  return items.filter(Boolean).map(item => {
    if (typeof item === 'string') return {code: '', text: item}
    return {
      code: item.code || '',
      text: item.details || item.message || item.warning || String(item.code || '')
    }
  })
}

function financialValues(source) {
  return {
    selling: source.selling_amount,
    profit: source.recognized_profit_amount,
    residual: source.story_derived_residual_amount,
    discount: source.source_discount_amount
  }
}

function amountRows(source, fields = DETAIL_AMOUNT_FIELDS) {
  return fields.map(([label, key]) => ({label, value: source[key]}))
}

export default {
  name: 'ClientsGroupsCostBreakdown',
  components: {AppTabs},
  data() {
    return {
      is_loading: true,
      load_failed: false,
      payload: null,
      active_view: 'categories',
      component_search: '',
      detail: {
        eyebrow: '',
        title: '',
        description: '',
        warnings: [],
        amounts: [],
        comparison: [],
        meta: []
      }
    }
  },
  computed: {
    normalization() {
      return this.payload?.normalization || null
    },
    source() {
      return this.payload?.source || {}
    },
    components_totals() {
      return this.normalization?.components_totals || null
    },
    currency() {
      return this.normalization?.currency || this.components_totals?.currency || 'USD'
    },
    normalization_status() {
      return normalizeStatus(this.normalization?.status)
    },
    normalization_warnings() {
      return normalizeWarnings(this.normalization?.warnings)
    },
    state_banner() {
      if (this.source.is_active === false) {
        return {
          title: 'Archived Story program',
          text: 'These are saved historical amounts. This program is no longer active in Story.',
          tone: 'neutral'
        }
      }
      if (this.normalization_warnings.length) {
        return {
          title: 'This calculation needs review',
          text: this.normalization_warnings[0].text,
          tone: 'amber'
        }
      }
      return null
    },
    source_meta() {
      if (!this.payload) return []
      const items = []
      if (this.source.story_program_id) items.push(`Story #${this.source.story_program_id}`)
      if (this.source.revision_id) items.push(`Revision #${this.source.revision_id}`)
      if (this.normalization?.id) items.push(`Normalization #${this.normalization.id}`)
      if (this.source.last_seen_at) items.push(`Last synced ${this.formatDateTime(this.source.last_seen_at)}`)
      return items
    },
    empty_state() {
      if (this.load_failed) {
        return {
          title: 'Unable to load cost breakdown',
          text: 'The Story cost request failed. Try opening the tab again.'
        }
      }
      return UNAVAILABLE_STATES[this.payload?.unavailable_reason] || {
        title: 'Story cost is not available',
        text: 'There is no saved Story cost calculation for this client group.'
      }
    },
    normalization_metrics() {
      if (!this.normalization) return []
      return [
        {
          label: 'Client total',
          value: this.normalization.folio_grand_total,
          description: `${this.formatMoney(this.normalization.selling_before_personal_discount)} selling · ${this.formatMoney(this.normalization.personal_discount_folio_ceiled)} personal discount`
        },
        {
          label: 'Story residual',
          value: this.normalization.story_derived_residual,
          description: 'Selling − recognized profit · planned estimate'
        },
        {
          label: 'Reported profit',
          value: this.normalization.reported_total_profit,
          description: `${this.formatMoney(this.normalization.story_commission_profit)} commission · ${this.formatMoney(this.normalization.personal_discount_folio_ceiled)} personal discount`
        }
      ]
    },
    view_tabs() {
      return [
        {id: 'categories', value: 'categories', label: `Categories (${this.category_rows.length})`},
        {id: 'components', value: 'components', label: `Components (${this.components.length})`},
        {id: 'summary', value: 'summary', label: `Component Summary (${this.summary.length})`}
      ]
    },
    categories() {
      return this.normalization?.categories || []
    },
    components() {
      return this.normalization?.components || []
    },
    summary() {
      return this.normalization?.components_summary || []
    },
    category_names() {
      return this.categories.reduce((result, category) => {
        result[category.id] = category.cost_category?.name || '-'
        return result
      }, {})
    },
    active_columns() {
      return VIEW_COLUMNS[this.active_view]
    },
    active_rows() {
      if (this.active_view === 'components') return this.component_rows
      if (this.active_view === 'summary') return this.summary_rows
      return this.category_rows
    },
    category_rows() {
      return this.categories.filter(category => Number(category.components_count) > 0).map(category => {
        const status = this.categoryStatus(category)
        return {
          id: category.id,
          entity_name: category.cost_category?.name || category.description || '-',
          lines: category.components_count,
          ...financialValues(category),
          status_label: status.label,
          status_tone: status.tone
        }
      })
    },
    component_rows() {
      const rows = this.components.map(component => {
        const status = normalizeStatus(component.mapping_status, 'unmapped')
        return {
          id: component.id,
          entity_name: component.description || component.line_key || '-',
          category_name: this.category_names[component.category_id] || '-',
          component_name: component.cost_component?.name || 'Not mapped',
          ...financialValues(component),
          status_label: status.label,
          status_tone: status.tone
        }
      })
      const search = String(this.component_search || '').trim().toLowerCase()
      if (!search) return rows
      return rows.filter(row => row.entity_name.toLowerCase().includes(search))
    },
    summary_rows() {
      return this.summary.map((summary, index) => ({
        id: summary.cost_component?.id || `unmapped-${index}`,
        entity_name: summary.cost_component?.name || 'Unmapped components',
        lines: summary.components_count,
        ...financialValues(summary)
      }))
    },
    active_footer() {
      const totals = this.components_totals || {}
      if (this.active_view === 'categories') {
        return {
          entity_name: 'All categories',
          lines: this.components.length,
          selling: this.normalization.selling_before_personal_discount,
          profit: this.normalization.recognized_profit_before_personal_discount,
          residual: this.normalization.story_derived_residual,
          discount: this.normalization.story_day_trip_discount,
          status: 'Before personal discount'
        }
      }
      return {
        entity_name: this.active_view === 'components' ? 'All component lines' : 'All components',
        category_name: '',
        component_name: '',
        lines: totals.components_count,
        selling: totals.selling_amount,
        profit: totals.recognized_profit_amount,
        residual: totals.story_derived_residual_amount,
        discount: totals.source_discount_amount,
        status: hasValue(totals.mapped_components_count) ? `${totals.mapped_components_count} mapped` : '-'
      }
    }
  },
  watch: {
    '$route.params.id': 'fetchStoryCost'
  },
  created() {
    this.fetchStoryCost()
  },
  methods: {
    fetchStoryCost() {
      this.is_loading = true
      this.load_failed = false
      this.payload = null
      this.component_search = ''
      getClientsGroupStoryCost(this.$route.params.id)
        .then(response => {
          this.payload = response.data
        })
        .catch(() => {
          this.load_failed = true
        })
        .finally(() => {
          this.is_loading = false
        })
    },
    displayValue(value) {
      return hasValue(value) ? value : '-'
    },
    formatAmount(value) {
      if (!hasValue(value)) return '—'
      const match = String(value).trim().match(/^(-)?(\d*)(?:\.(\d*))?$/)
      if (!match || (!match[2] && !match[3])) return String(value)
      const sign = match[1] ? '−' : ''
      const integer = (match[2] || '0').replace(/^0+(?=\d)/, '')
      const decimal = (match[3] || '').padEnd(2, '0').slice(0, 2)
      return `${sign}${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimal}`
    },
    formatMoney(value) {
      const amount = this.formatAmount(value)
      return amount === '—' ? amount : `${amount} ${this.currency}`
    },
    formatDateTime(value) {
      try {
        return format(parseISO(value), 'dd MMM yyyy HH:mm')
      } catch (error) {
        return value
      }
    },
    categoryStatus(category) {
      if (normalizeWarnings(category.warnings).length) return STATUS_LABELS.review_required
      if (category.story_bucket === 'other' && !this.isZero(category.profit_reconciliation_delta)) {
        return {label: 'Category-only profit', tone: 'neutral'}
      }
      const deltaFields = [
        'selling_reconciliation_delta',
        'profit_reconciliation_delta',
        'discount_reconciliation_delta',
        'residual_reconciliation_delta'
      ]
      if (deltaFields.some(field => hasValue(category[field]) && !this.isZero(category[field]))) {
        return {label: 'Has delta', tone: 'amber'}
      }
      return STATUS_LABELS.reconciled
    },
    isZero(value) {
      return Number(value) === 0
    },
    setDetail(detail) {
      this.detail = {
        eyebrow: detail.eyebrow || '',
        title: detail.title || '',
        description: detail.description || '',
        warnings: detail.warnings || [],
        amounts: detail.amounts || [],
        comparison: detail.comparison || [],
        meta: detail.meta || []
      }
      this.$modal.show('story-cost-details')
    },
    openWarningsDetails() {
      this.setDetail({
        eyebrow: this.source.revision_id ? `Revision #${this.source.revision_id}` : 'Story calculation',
        title: 'Calculation issues',
        warnings: this.normalization_warnings
      })
    },
    openNormalizationDetails() {
      this.setDetail({
        eyebrow: this.normalization.id ? `Normalization #${this.normalization.id}` : 'Normalization',
        title: 'Program calculation',
        description: 'The saved values from the current Story revision are shown without rebuilding totals in Board.',
        warnings: this.normalization_warnings,
        amounts: amountRows(this.normalization, MONEY_FIELDS),
        meta: [
          this.source.lead_id ? `Lead #${this.source.lead_id} · Itinerary #${this.source.itinerary_id} · Story #${this.source.story_program_id}` : '',
          this.source.revision_id ? `Revision #${this.source.revision_id} · ${this.normalization.status}` : ''
        ].filter(Boolean)
      })
    }
  }
}
</script>

<style lang="sass" scoped>
.cost-breakdown
  position: relative
  min-height: 220px

.cost-breakdown__heading,
.cost-breakdown__table-heading
  display: flex
  align-items: flex-start
  justify-content: space-between
  gap: 20px

.cost-breakdown__heading
  margin-bottom: 12px

  .title
    margin: 0

.cost-breakdown__title-row
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 10px

.cost-breakdown__description,
.cost-breakdown__details-description
  margin: 8px 0 0
  color: $color-text-second
  line-height: 1.45

.cost-breakdown__details-description
  margin-bottom: 16px

.cost-breakdown__status
  display: inline-flex
  min-height: 26px
  align-items: center
  padding: 4px 9px
  border-radius: 5px
  background-color: #ededed
  color: #5f5f5f
  font-size: 12px
  font-weight: 600
  white-space: nowrap

.cost-breakdown__status--small
  min-height: 22px
  padding: 3px 7px
  font-size: 11px

.cost-breakdown__status--green
  background-color: rgba($color-theme, .14)
  color: #56830a

.cost-breakdown__status--amber
  background-color: #fff0c2
  color: #8d6a00

.cost-breakdown__status--red
  background-color: rgba($color-error, .14)
  color: #c23c3c

.cost-breakdown__meta
  display: flex
  flex-wrap: wrap
  gap: 8px 20px
  color: #7d7d7d
  font-size: 12px

  span
    position: relative

    &:not(:last-child):after
      content: '·'
      position: absolute
      right: -12px

.cost-breakdown__banner
  display: flex
  align-items: center
  justify-content: space-between
  gap: 20px
  margin-bottom: 20px
  padding: 14px 16px
  border: 1px solid #ead27d
  border-radius: 8px
  background-color: #fff9e8

  p
    margin: 4px 0 0
    color: $color-text-second

.cost-breakdown__banner--neutral
  border-color: #d7d7d7
  background-color: #f7f7f7

.cost-breakdown__link-button
  flex-shrink: 0
  padding: 0
  border: 0
  background: transparent
  color: $color-theme
  font-size: 13px
  font-weight: 600
  cursor: pointer

  &:hover
    color: #63950f

.cost-breakdown__metrics
  flex-wrap: nowrap
  gap: 20px
  margin: 20px 0

.cost-breakdown__metric
  display: flex
  max-width: none
  flex: 1 1 0
  flex-direction: column
  gap: 8px

.cost-breakdown__metric-label
  color: $color-text-second

.cost-breakdown__metric-value
  font-size: 24px
  font-weight: 500

.cost-breakdown__metric-description
  color: #7d7d7d
  font-size: 12px
  line-height: 1.4

.cost-breakdown__table-heading
  align-items: flex-start
  margin-bottom: 10px

  .cost-breakdown__tabs
    flex: 1 1 auto
    margin-bottom: 0

.cost-breakdown__unit
  flex-shrink: 0
  padding-top: 8px
  color: #7d7d7d
  font-size: 12px

.cost-breakdown__search
  margin-bottom: 12px

.cost-breakdown__table
  width: 100%

  th,
  td
    white-space: normal

  tfoot
    border: 1px solid #eaeaea
    background-color: #f3f3f3

    td
      padding: 9px 12px
      text-align: center

.cost-breakdown__cell--left
  text-align: left !important

.cost-breakdown__amount
  font-variant-numeric: tabular-nums

.cost-breakdown__details-eyebrow
  margin-bottom: 5px
  color: #7d7d7d
  font-size: 11px
  font-weight: 600
  text-transform: uppercase

.cost-breakdown__detail-warning
  display: flex
  align-items: flex-start
  gap: 10px
  margin-top: 12px
  padding: 10px 12px
  border-radius: 6px
  background-color: #fff0c2
  line-height: 1.4

.cost-breakdown__details-list
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 0 24px
  margin: 20px 0

  div
    display: flex
    align-items: center
    justify-content: space-between
    gap: 16px
    padding: 10px 0
    border-bottom: 1px solid #eaeaea

  dt
    color: $color-text-second

  dd
    margin: 0
    font-weight: 600
    font-variant-numeric: tabular-nums
    text-align: right

.cost-breakdown__details-table
  table
    table-layout: fixed

  th,
  td
    white-space: normal

  th:first-child,
  td:first-child
    width: 34%

.cost-breakdown__details-meta
  margin-top: 20px
  padding: 12px
  border-radius: 6px
  background-color: #f7f7f7
  color: $color-text-second
  font-size: 12px

  p
    margin: 0 0 6px
    overflow-wrap: anywhere

    &:last-child
      margin-bottom: 0

@media (max-width: 1000px)
  .cost-breakdown__metrics
    flex-wrap: wrap

  .cost-breakdown__metric
    min-width: calc(50% - 10px)

@media (max-width: 700px)
  .cost-breakdown__heading,
  .cost-breakdown__table-heading,
  .cost-breakdown__banner
    align-items: stretch
    flex-direction: column

  .cost-breakdown__heading .btn
    align-self: flex-start

  .cost-breakdown__unit
    padding-top: 0

  .cost-breakdown__metric
    min-width: 100%

  .cost-breakdown__details-list
    grid-template-columns: 1fr
</style>
